// ---------------------------------------------------------------------------
// canEditResource — single source of truth for who may edit/delete a workspace
// resource (agent, grader, eval template, judge). Shared across every page so
// the edit/delete permission UX stays aligned.
//
// Rule:
//   • Public workspace ("public") is a shared sandbox — anyone may edit.
//   • Any other workspace is owned: only the resource's creator or the
//     workspace's creator may edit. A signed-out user may never edit.
//
// Identity comparison mirrors the rest of the app (systemProperties.createdBy
// vs. the signed-in account's username / UPN).
// ---------------------------------------------------------------------------

import { DEFAULT_WORKSPACE_NAME } from '../constants/index.js';

export interface CanEditResourceInput {
  /** Workspace the resource lives in. `"public"` grants edit to everyone. */
  workspaceName: string;
  /** Signed-in user's identity (`useAuth().account?.username`), or null. */
  currentUser: string | null;
  /** Resource creator (`systemProperties.createdBy`); may be unknown. */
  resourceCreatedBy?: string | null;
  /** Workspace creator (`workspace.systemProperties.createdBy`); may be unknown. */
  workspaceCreatedBy?: string | null;
}

export function canEditResource({
  workspaceName,
  currentUser,
  resourceCreatedBy,
  workspaceCreatedBy,
}: CanEditResourceInput): boolean {
  // Public workspace: shared sandbox, no ownership gating.
  if (workspaceName === DEFAULT_WORKSPACE_NAME) return true;

  // Owned workspace: a known identity is required to match a creator.
  if (currentUser == null) return false;

  // UPNs/usernames are case-insensitive, so normalize before comparing;
  // otherwise an owner whose stored `createdBy` differs only in casing
  // (e.g. `Alice@x.com` vs `alice@x.com`) would be wrongly denied.
  const normalizeIdentity = (value: string | null | undefined): string | null =>
    value?.trim().toLowerCase() ?? null;

  const user = normalizeIdentity(currentUser);
  const resourceOwner = normalizeIdentity(resourceCreatedBy);
  const workspaceOwner = normalizeIdentity(workspaceCreatedBy);

  return user != null && (resourceOwner === user || workspaceOwner === user);
}
